// libraries
import type { StateTree, AIVehicleRecommendation } from '@makemydeal/dr-dash-types';
import { LENDER_CREDIT_SOURCE_SOFT_PULL } from '@makemydeal/dr-dash-types';
import type { FeatureTogglesAndUrlOnly } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// interfaces/types
import type { AlertSeverity } from '@interstate/components/Alert/Types/alertTypes';

// selectors
import { getSelectedTerm, hasMissingPaymentData, getAppliedIncentives } from './offerRedux';
import { getShowSaveReminder as getShowCreditAppSearchSaveReminder } from './creditAppSearchSelectors';
import { getHasVehicle } from './vehicleSelectors';
import { DateTime } from 'luxon';

// actions
import { openSlideOut, SlideOutType } from '../actions/baseDealActionCreators';

// utils
import { filterByEndDate } from '../utils/incentivesUtil';
import {
    ATTACH_CREDIT_APP_SAVE_REMINDER,
    DOC_MOVE_FAILURE_MESSAGE,
    DOC_MOVE_INITIATED_MESSAGE,
    DOC_MOVE_SUCCESS_MESSAGE
} from '../constants';
import { getDocMoveWsEvent } from './docMoveSelectors';
import { clearDocMoveAlert } from '../actions/docMoveActionCreators';

export const wasTheLastFailure = (actionTypeToCheck: string) => (state: StateTree) =>
    state.desking.lastFailureAction === actionTypeToCheck;

export const featureEnabled = (name: keyof FeatureTogglesAndUrlOnly) => (state: StateTree) =>
    featureToggleSelectors.getFeatureToggle(state, name);

/**
 * Get the selected term (with current offertype) and its ID for select options.
 * @returns {string} Selected term Id
 */
export const getSelectedTermId = (state: any) => getSelectedTerm(state)?.id;

export type AlertMessage = {
    message: string;
    /**
     * Alert type; `warning`, `info`, `error`, `success`
     */
    type: AlertSeverity;
    /**
     * Call to action text to appear on the button
     */
    cta?: string;
    /**
     * Provide an action to dispatch. (Typically this is our navigationActionTypes or legacy actions from activity packages).
     */
    ctaAction?: any;
    /**
     * Title if necessary; just passes through to the Interstate Alert.
     */
    title?: string;
    /**
     * Action to dispatch to clear this alert from state. Used with `autoDismissMs` for transient alerts.
     */
    clearAction?: any;
    /**
     * Time in milliseconds before automatically dispatching `clearAction`. Requires `clearAction` to be set.
     */
    autoDismissMs?: number;
};

/**
 * Aggregates alerts that should be displayed atop desking
 */
export const getDeskingAlerts = (state: any): AlertMessage[] => {
    // NOTE: Evaluate for expired incentives
    const messages: AlertMessage[] = [];

    if (shouldDisplayMissingPaymentDataError(state)) {
        messages.push({
            type: 'error',
            message: 'Missing Payment Details',
            title: 'Important Message About Payment Calculation'
        });
    }

    if (hasExpiredIncentives(state)) {
        messages.push({
            type: 'caution',
            message: 'Some items attached to this deal have expired.',
            ctaAction: openSlideOut(SlideOutType.Incentives),
            cta: 'Update now',
            title: 'Important Message About Programs & Incentives'
        });
    }

    if (getShowCreditAppSearchSaveReminder(state)) {
        messages.push({
            type: 'info',
            message: ATTACH_CREDIT_APP_SAVE_REMINDER
        });
    }

    const docMoveEvent = getDocMoveWsEvent(state);

    if (docMoveEvent?._type === 'docMoveEventInitiated') {
        messages.push({
            type: 'info',
            message: DOC_MOVE_INITIATED_MESSAGE
        });
    }

    if (docMoveEvent?._type === 'docMoveEventSuccess' && docMoveEvent.status === 'success') {
        messages.push({
            type: 'success',
            message: DOC_MOVE_SUCCESS_MESSAGE,
            clearAction: clearDocMoveAlert(),
            autoDismissMs: 5500
        });
    }

    if (docMoveEvent?._type === 'docMoveEventFailure' && docMoveEvent.status === 'failure') {
        messages.push({
            type: 'error',
            message: DOC_MOVE_FAILURE_MESSAGE
        });
    }

    return messages;
};

/**
 * Returns true if any of the incentivees in the active selection are expired.
 * Expiration: hard wired to seven days before today.
 */
export const hasExpiredIncentives = (state: StateTree): boolean => {
    const incentives = getAppliedIncentives(state);
    const lowerLimit = DateTime.now().startOf('day').minus({ days: 7 }).toISO();
    const filterCheck = filterByEndDate(lowerLimit!); // NOTE: using same filter as the screen
    const total = incentives.reduce((sum, current) => sum + (!filterCheck({ endDate: current.endDate }) ? 1 : 0), 0);
    return total > 0;
};

// Acceleron sets `missingPaymentData` whenever the payment orchestrator returned nothing, which
// conflates "the calculation failed" with "there was nothing to calculate". A deal with no vehicle is
// the latter, so the alert claimed a failure that never happened (DE770258).
export const shouldDisplayMissingPaymentDataError = (state: StateTree): boolean => {
    if (featureToggleSelectors.isPaymentAlertVehicleAwareEnabled(state)) {
        return hasMissingPaymentData(state) && getHasVehicle(state);
    }
    return hasMissingPaymentData(state);
};

export const shouldShowTotalTaxRateSupersedeAlert = (state: StateTree): boolean => {
    return !!state.desking?.showTotalTaxRateSupersedeAlert;
};

export const getPSErrorDetails = (state: StateTree) => state.desking.psErrorDetails;

export const hasForceReloadOnWSEventTriggered = (state: StateTree) => {
    return state.desking.hasForceReloadOnWSEventTriggered || false;
};

export const getForceReloadSource = (state: StateTree): string | undefined => {
    return state.desking?.forceReloadSource;
};

export const getForceReloadMessage = (state: StateTree): string | undefined => {
    return state.desking?.forceReloadMessage;
};

// AI Lender Probabilities Selectors
export const getQualificationIds = (state: StateTree) => state.desking?.qualificationIds ?? [];

export const getLenderProbabilities = (state: StateTree) => state.desking?.lenderProbabilities;

export const getLenderProbabilitiesLoading = (state: StateTree) => state.desking?.lenderProbabilitiesLoading || false;

// Aggregate affordability fields surfaced at the lender-probability response root (drive the card).
// Defaulted here so consumers always get a stable typed value.
export const getLenderAffordabilityLevel = (state: StateTree): 'High' | 'Limited' =>
    state.desking?.lenderAffordabilityLevel ?? 'Limited';

export const getLenderPredictedRate = (state: StateTree): string => state.desking?.lenderPredictedRate ?? '';

export const getTopLenderData = (state: StateTree) => state.desking?.lenderProbabilities?.[0];

export const getLenderCreditSource = (state: StateTree) => state.desking?.lenderCreditSource;

export const getIsSoftPull = (state: StateTree): boolean => getLenderCreditSource(state) === LENDER_CREDIT_SOURCE_SOFT_PULL;

export const getSoftPullCreditScore = (state: StateTree) => state.desking?.softPullCreditScore;

// AI Vehicle Recommendations Selectors
export const getAiVehicleRecommendations = (state: StateTree): AIVehicleRecommendation[] =>
    state.desking?.aiVehicleRecommendations || [];

export const getAiVehicleRecommendationsLoading = (state: StateTree): boolean =>
    state.desking?.aiVehicleRecommendationsLoading || false;

export const hasAiVehicleRecommendations = (state: StateTree): boolean => getAiVehicleRecommendations(state).length > 0;

// AI Vehicle Recommendations CTA Selectors
export const shouldShowAiRecommendationsCTA = (state: StateTree): boolean => {
    const isLoading = getAiVehicleRecommendationsLoading(state);
    return !isLoading;
};

export const isAiRecommendationsCTAAvailable = (state: StateTree): boolean => {
    const isLoading = getAiVehicleRecommendationsLoading(state);
    const hasRecommendations = hasAiVehicleRecommendations(state);
    return !isLoading && hasRecommendations;
};

export const getShouldShowAIRefreshButton = (state: StateTree): boolean => {
    return state.desking?.shouldShowAIRefreshButton || false;
};
